/**
 * Компонент заголовка (шапка)
 * 
 * Отображает: логотип, ссылки меню, имя пользователя, кнопку выхода
 */

import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getPendingApprovalPaymentRequestsCount,
  getPendingIssuePaymentRequestsCount,
} from '../services/api';
import { PAYMENT_APPROVALS_CHANGED_EVENT } from '../utils/paymentApprovalsEvents';
import { getAccessibleDashboards, getHomePath, isDashboardPath } from '../utils/dashboards';
import '../styles/Header.css';

type IconProps = {
  path: string | string[];
};

const Icon: React.FC<IconProps> = ({ path }) => {
  const ds = Array.isArray(path) ? path : [path];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {ds.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
};

type SubmenuLink = { type: 'link'; label: string; to?: string; badge?: boolean };
type SubmenuGroup = {
  type: 'group';
  label: string;
  items: Array<{ label: string; to?: string; badge?: boolean }>;
};
type SubmenuEntry = SubmenuLink | SubmenuGroup;

type MenuItem = {
  id: string;
  label: string;
  iconPath: string | string[];
  to?: string;
  /** Скрыть общий подзаголовок попапа (когда заголовки задают группы). */
  hideSubmenuTitle?: boolean;
  /** Заголовок попапа; если не задан — используется label кнопки. */
  submenuTitle?: string;
  children?: SubmenuEntry[];
};

const Header: React.FC = () => {
  const { user, logout, can, hasDepartment, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openedMenuId, setOpenedMenuId] = useState<string | null>(null);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [pendingIssuesCount, setPendingIssuesCount] = useState(0);
  const sidebarRef = useRef<HTMLElement | null>(null);

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const canViewDirectory = hasDepartment('ALL_COMP');
  const canIntoWork =
    canViewDirectory &&
    can('CONTRACTORS', 'CREATE') &&
    can('CONTRACTS', 'CREATE') &&
    can('TASKS', 'CREATE');
  const canViewInProgressContracts =
    hasDepartment('OP', 'OUP_UL', 'ALL_COMP') && can('OUTCOME', 'VIEW');
  const canViewInProgressTasks = can('TASKS', 'VIEW') && can('OUTCOME', 'VIEW');
  const canViewClosingTasks = can('TASKS_CLOSING', 'VIEW');
  const canViewClosingContracts = can('CONTRACTS_CLOSING', 'CREATE');
  const canManageClosing = can('TASKS_CLOSING', 'EDIT');
  const canViewOperations = can('OPERATIONS_SYNC', 'EDIT');
  const canCreatePaymentRequest = can('OUTCOME_REQUEST', 'CREATE');
  const canViewPaymentRequests = can('OUTCOME_REQUEST', 'VIEW');
  const canApprovePaymentRequests = can('OUTCOME_REQUEST', 'EDIT');
  const canIssuePayments = can('OUTCOME_ISSUE', 'EDIT');
  const canDistributeIncome = can('INCOME', 'EDIT');
  const canViewIncome = can('INCOME', 'VIEW');
  const canViewAccessControl = can('ACCESS_ADMIN', 'VIEW');
  const canViewMasterdata = can('MASTERDATA', 'EDIT');
  const canCountApprovals = canApprovePaymentRequests;
  const canCountIssues = canIssuePayments;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent): void => {
      if (!sidebarRef.current) {
        return;
      }
      const target = event.target as Node;
      if (!sidebarRef.current.contains(target)) {
        setOpenedMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || (!canCountApprovals && !canCountIssues)) {
      setPendingApprovalsCount(0);
      setPendingIssuesCount(0);
      return;
    }

    let cancelled = false;
    let inFlight = false;

    const loadCount = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      try {
        const [approvalsResult, issuesResult] = await Promise.allSettled([
          canCountApprovals
            ? getPendingApprovalPaymentRequestsCount()
            : Promise.resolve(0),
          canCountIssues
            ? getPendingIssuePaymentRequestsCount()
            : Promise.resolve(0),
        ]);
        if (!cancelled) {
          setPendingApprovalsCount(
            approvalsResult.status === 'fulfilled' ? approvalsResult.value : 0,
          );
          setPendingIssuesCount(
            issuesResult.status === 'fulfilled' ? issuesResult.value : 0,
          );
        }
      } finally {
        inFlight = false;
      }
    };

    void loadCount();

    const onChanged = (): void => {
      void loadCount();
    };
    const onFocusOrVisible = (): void => {
      if (document.visibilityState === 'hidden') return;
      void loadCount();
    };

    window.addEventListener(PAYMENT_APPROVALS_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadCount();
    }, 10000);

    return () => {
      cancelled = true;
      window.removeEventListener(PAYMENT_APPROVALS_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, canCountApprovals, canCountIssues]);

  const paymentRequestItems = [
    ...(canCreatePaymentRequest
      ? [{ label: 'Подать заявку', to: '/finance/payment-requests' }]
      : []),
    ...(canViewPaymentRequests
      ? [{ label: 'Посмотреть заявки', to: '/finance/payment-requests-view' }]
      : []),
    ...(canApprovePaymentRequests
      ? [{
          label: 'Согласовать заявку',
          to: '/finance/payment-approvals',
          badge: pendingApprovalsCount > 0,
        }]
      : []),
    ...(canIssuePayments
      ? [{
          label: 'Выдать ДС',
          to: '/finance/payment-issues',
          badge: pendingIssuesCount > 0,
        }]
      : []),
  ];

  const incomeJournalItems = [
    ...(canDistributeIncome
      ? [{ label: 'Распределить поступления', to: '/finance/payment-distribution' }]
      : []),
    ...(canViewIncome
      ? [{ label: 'Посмотреть поступления', to: '/finance/payment-view' }]
      : []),
  ];

  const financeChildren: SubmenuEntry[] = [
    ...(canViewOperations
      ? [{
          type: 'group' as const,
          label: 'Операции из ПФ',
          items: [{ label: 'Операции', to: '/operations' }],
        }]
      : []),
    ...(paymentRequestItems.length > 0
      ? [{ type: 'group' as const, label: 'Заявки на ДС', items: paymentRequestItems }]
      : []),
    ...(incomeJournalItems.length > 0
      ? [{ type: 'group' as const, label: 'Журнал поступлений', items: incomeJournalItems }]
      : []),
  ];

  const inProgressChildren: SubmenuEntry[] = [
    ...(canViewInProgressTasks
      ? [{ type: 'link' as const, label: 'Задачи', to: '/in-progress/tasks' }]
      : []),
    ...(canViewInProgressContracts
      ? [{ type: 'link' as const, label: 'Договоры (ОП/ОУП)', to: '/in-progress/contracts' }]
      : []),
  ];

  const closingChildren: SubmenuEntry[] = [
    ...(canViewClosingTasks
      ? [{ type: 'link' as const, label: 'Закрытие задач', to: '/closing/tasks' }]
      : []),
    ...(canViewClosingContracts
      ? [
          { type: 'link' as const, label: 'Закрытие этапов', to: '/closing/stages' },
          { type: 'link' as const, label: 'Закрытие договоров', to: '/closing/contracts' },
        ]
      : []),
    ...(canManageClosing
      ? [{ type: 'link' as const, label: 'Даты закрытия', to: '/closing/managment' }]
      : []),
  ];

  const settingsChildren: SubmenuEntry[] = [
    ...(canViewAccessControl
      ? [
          { type: 'link' as const, label: 'Роли и Модули', to: '/admin/access' },
          { type: 'link' as const, label: 'к Статьям Расходов', to: '/settings/access/articles' },
        ]
      : []),
    ...(canViewMasterdata
      ? [{
          type: 'group' as const,
          label: 'Мастерданные',
          items: [
            { label: 'Статьи', to: '/settings/masterdata/articles' },
            { label: 'Налоги', to: '/settings/masterdata/taxes' },
            { label: 'Кэш', to: '/settings/masterdata/cash' },
            { label: 'Сервисный сбор', to: '/settings/masterdata/serv-fee' },
          ],
        }]
      : []),
  ];

  const accessibleDashboards = getAccessibleDashboards(hasDepartment);
  const homePath = getHomePath(accessibleDashboards);
  const isHomeActive =
    location.pathname === '/' || isDashboardPath(location.pathname, accessibleDashboards);

  const homeItem: MenuItem =
    accessibleDashboards.length > 1
      ? {
          id: 'home',
          label: 'Главная',
          iconPath: 'M3 10.5 12 3l9 7.5V21h-6v-6H9v6H3z',
          submenuTitle: 'Дашборды',
          children: accessibleDashboards.map((dashboard) => ({
            type: 'link' as const,
            label: dashboard.label,
            to: dashboard.to,
          })),
        }
      : {
          id: 'home',
          label: 'Главная',
          to: homePath,
          iconPath: 'M3 10.5 12 3l9 7.5V21h-6v-6H9v6H3z',
        };

  const menuItems: MenuItem[] = [
    homeItem,
    ...(canViewDirectory
      ? [{
          id: 'contracts',
          label: 'Справочник',
          iconPath: 'M4 5h16v14H4zM8 9h8M8 13h8',
          children: [
            ...(canIntoWork
              ? [{ type: 'link' as const, label: 'Создать запись', to: '/register/create' }]
              : []),
            { type: 'link' as const, label: 'Контрагенты', to: '/contractors' },
            { type: 'link' as const, label: 'Договоры', to: '/contracts' },
            { type: 'link' as const, label: 'Задачи', to: '/tasks' },
          ],
        }]
      : []),
    ...(inProgressChildren.length > 0
      ? [{
          id: 'in-progress',
          label: 'В работе',
          iconPath: [
            'M4 6h16v12H4z',
            'M8 10h8M8 14h5',
            'M9 3h6v3H9z',
          ],
          children: inProgressChildren,
        }]
      : []),
    ...(closingChildren.length > 0
      ? [{
          id: 'closing',
          label: 'Закрытие',
          iconPath: [
            'M15.2 6.2a3.2 3.2 0 1 1-6.4 0 3.2 3.2 0 0 1 6.4 0',
            'M12 9.6v6.2',
            'M12 15.8 8 21.5',
            'M12 15.8 16 21.5',
            'M12 11.4 5.5 4.8',
            'M12 11.4 18.5 4.8',
          ],
          children: closingChildren,
        }]
      : []),
    ...(financeChildren.length > 0
      ? [{
          id: 'finance',
          label: 'Финансы',
          hideSubmenuTitle: true,
          iconPath: [
            'M3 7h18v10H3z',
            'M7 7V5h10v2',
            'M7 17v2h10v-2',
            'M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
          ],
          children: financeChildren,
        }]
      : []),
    ...(settingsChildren.length > 0
      ? [{
          id: 'settings',
          label: 'Настройки',
          submenuTitle: 'Доступы',
          iconPath: [
            'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.644.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.008-.378-.137-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.75.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.213-1.281z',
            'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
          ],
          children: settingsChildren,
        }]
      : []),
  ];

  return (
    <div className="sidebar-root">
      <header className="top-header">
        <div className="top-header__left">
          <img src="/logo-mark.png" alt="Универстрой" className="top-logo" />
        </div>
        <div className="top-header__brand" aria-label="UniCore — информационное ядро компании">
          <span className="top-header__brand-name">UniCore</span>
          <span className="top-header__brand-tagline">информационное ядро компании</span>
        </div>
        <div className="top-header__right">
          <span className="top-header__login">{user?.username}</span>
          <button type="button" className="top-logout-button" onClick={handleLogout} aria-label="Выйти" title="Выйти">
            <Icon path="M10 4H5v16h5M14 8l5 4-5 4M19 12H9" />
          </button>
        </div>
      </header>

      <aside className="sidebar" ref={sidebarRef}>
        <nav className="icon-menu" id="app-sidebar-nav">
          {menuItems.map((item) => {
            if (item.children && item.children.length > 0) {
              const isOpened = openedMenuId === item.id;
              const isGroupActive = item.id === 'home' && isHomeActive;
              return (
                <div key={item.id} className={`menu-group${isOpened ? ' menu-group--open' : ''}`}>
                  <button
                    type="button"
                    className={`menu-icon-button${isGroupActive ? ' active' : ''}`}
                    title={item.label}
                    aria-label={item.label}
                    onClick={() => setOpenedMenuId((prev) => (prev === item.id ? null : item.id))}
                  >
                    <span className="menu-icon-button__icon-wrap">
                      <Icon path={item.iconPath} />
                      {item.id === 'finance' && (pendingApprovalsCount > 0 || pendingIssuesCount > 0) ? (
                        <span className="menu-nav-dot" aria-hidden />
                      ) : null}
                    </span>
                    <span className="menu-icon-label">{item.label}</span>
                  </button>
                  <div className="submenu-popup">
                    {!item.hideSubmenuTitle ? (
                      <div className="submenu-title">{item.submenuTitle ?? item.label}</div>
                    ) : null}
                    <div className="submenu-list">
                      {item.children.map((child) => {
                        if (child.type === 'group') {
                          return (
                            <div key={child.label} className="submenu-group">
                              <div className="submenu-title">{child.label}</div>
                              {child.items.map((groupItem) =>
                                groupItem.to ? (
                                  <NavLink
                                    key={groupItem.label}
                                    to={groupItem.to}
                                    className="submenu-link"
                                    onClick={() => setOpenedMenuId(null)}
                                  >
                                    <span>{groupItem.label}</span>
                                    {groupItem.badge ? <span className="menu-nav-dot" aria-hidden /> : null}
                                  </NavLink>
                                ) : (
                                  <button
                                    key={groupItem.label}
                                    type="button"
                                    className="submenu-link submenu-link--disabled"
                                    onClick={() => setOpenedMenuId(null)}
                                  >
                                    {groupItem.label}
                                  </button>
                                ),
                              )}
                            </div>
                          );
                        }
                        return child.to ? (
                          <NavLink
                            key={child.label}
                            to={child.to}
                            className="submenu-link"
                            onClick={() => setOpenedMenuId(null)}
                          >
                            <span>{child.label}</span>
                            {child.badge ? <span className="menu-nav-dot" aria-hidden /> : null}
                          </NavLink>
                        ) : (
                          <button
                            key={child.label}
                            type="button"
                            className="submenu-link submenu-link--disabled"
                            onClick={() => setOpenedMenuId(null)}
                          >
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            if (item.to) {
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === '/'}
                  title={item.label}
                  aria-label={item.label}
                  className={({ isActive }) => `menu-icon-button ${isActive ? 'active' : ''}`}
                  onClick={() => setOpenedMenuId(null)}
                >
                  <Icon path={item.iconPath} />
                  <span className="menu-icon-label">{item.label}</span>
                </NavLink>
              );
            }

            return (
              <button key={item.id} type="button" className="menu-icon-button" title={item.label} aria-label={item.label}>
                <Icon path={item.iconPath} />
                <span className="menu-icon-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>
    </div>
  );
};

export default Header;